import {Component, Inject, ViewChild, inject} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AbstractStepDTO,
  ConfiguratorDataViewService,
  SaveQueryStepDTO
} from '@tj/tj-dmp-lib-api';
import { SelectItem } from 'primeng/api';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SelectSaveQueryComponent } from '../../select-save-query/select-save-query.component';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import {
  ON_CANCEL_TOKEN,
  ON_SAVE_TOKEN,
  PARTNER_TOKEN,
  READ_ONLY_MODE_TOKEN,
  STEP_TOKEN,
  TENANT_TOKEN
} from '../injection-tokens';
import {resolveI18nLabel} from '../../../pipes/i18n-label.pipe';
import {LangService} from '../../../services/lang.service';
import { StepDescriptionService } from '../step-description.service';

@Component({
  selector: 'dmp-save-query-step-config',
  templateUrl: './save-query-step-config.component.html',
  standalone: false
})
export class SaveQueryStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);
  private readonly langService = inject(LangService);

  @ViewChild(SelectSaveQueryComponent) saveQuerySelector?: SelectSaveQueryComponent;

  saveQueryForm: FormGroup;
  dataViews: SelectItem[] = [];
  loadingDataViews = false;
  dataViewsLoadFailed = false;

  get saveQueryStep(): SaveQueryStepDTO {
    return this.step as SaveQueryStepDTO;
  }

  get codeDataView() {
    return this.saveQueryForm?.get('codeDataView');
  }

  get idSaveQuery() {
    return this.saveQueryForm?.get('idSaveQuery');
  }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: boolean,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private readonly formBuilder: FormBuilder,
    private readonly dataViewService: ConfiguratorDataViewService
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.loadDataViews();
  }

  protected updateStepFromForm(): void {
    if (!this.saveQueryStep || !this.saveQueryForm.valid) {
      return;
    }

    const { codeDataView, idSaveQuery } = this.saveQueryForm.getRawValue();
    this.saveQueryStep.codeDataView = codeDataView;
    this.saveQueryStep.idSaveQuery = idSaveQuery;
    this.saveQueryStep.description = this.generateReadableDescription(codeDataView, idSaveQuery);
    this.saveQueryStep.updatedAt = new Date();
  }

  protected isConfigurationValid(): boolean {
    return !!this.saveQueryForm
      && this.saveQueryForm.valid
      && !this.loadingDataViews
      && !this.dataViewsLoadFailed;
  }

  private createForm(): void {
    this.isInitializing = true;
    this.saveQueryForm = this.formBuilder.group({
      codeDataView: this.createFormControl(this.saveQueryStep?.codeDataView ?? null, [Validators.required]),
      idSaveQuery: this.createFormControl(this.saveQueryStep?.idSaveQuery ?? null, [Validators.required])
    });
    this.isInitializing = false;

    this.codeDataView.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.isInitializing || this.readOnlyMode) {
        return;
      }
      this.idSaveQuery.reset(null);
    });

    this.saveQueryForm.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.markAsChanged();
      }
    });
  }

  private loadDataViews(): void {
    this.loadingDataViews = true;
    this.dataViewsLoadFailed = false;

    this.dataViewService.getAllDataViewsUsingGET(this.tenant).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: dataViews => {
        this.dataViews = [...dataViews]
          .sort((a, b) => a.code.localeCompare(b.code))
          .map(dataView => ({
            label: resolveI18nLabel(dataView.labels, this.langService.currentLang, dataView.name),
            value: dataView.code
          }));
        this.loadingDataViews = false;
      },
      error: () => {
        this.dataViews = [];
        this.loadingDataViews = false;
        this.dataViewsLoadFailed = true;
      }
    });
  }

  private generateReadableDescription(codeDataView: string, idSaveQuery: string): string {
    const dataView = this.dataViews.find(item => item.value === codeDataView);
    const saveQuery = this.saveQuerySelector?.getCurrentSaveQuery();

    return this.stepDescriptionService.saveQuery(dataView?.label || codeDataView, saveQuery?.name || idSaveQuery);
  }
}
